import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Collapse, Grid, IconButton, MenuItem, Select, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { CardWrapper } from '../../UI';
import { COLUNAS } from '../Recalculos/recalculosProcessoController';
import * as C from '../../../theme/colors.constants';

/**
 * Aba Recálculos do Processo — print `grid_check_recalculos.png`.
 *
 * AW2-1093 Etapa 0: `recalculos` (array já mapeado pelo container via
 * `recalculosProcessoController.mapearRecalculos`) substitui o mock
 * estático `RECALCULOS`/paginação client-side simulada. Paginação mantém o
 * MESMO visual atual (setas prev/next + "Linhas por página", não
 * `TablePagination` do MUI — mesmo cuidado já documentado no módulo para
 * não divergir de um print sem necessidade) — só a fonte do estado muda de
 * local para as props do container (paginação real no servidor, sem
 * ordenação — confirmado por leitura direta de `ListaRecalculoProcesso.jsx`
 * real que esta grid não tem `sort`).
 *
 * Linha expansível (chevron) revela os 4 grupos de campos de valor (Juros,
 * Honorários, Descontos, Taxa do cartão) — as "demais colunas de valor já
 * existentes" do AC2, escondidas atrás do collapse na tela real também.
 * Mesma TÉCNICA já portada para a grid de Acordos (`TableAcordos.jsx`,
 * AW2-1133 Etapa 4: `Set` de ids expandidos local ao componente, `Collapse`
 * dentro de um `TableCell colSpan`), não um import direto (domínio de dado
 * diferente).
 */
export default function AbaRecalculos({
  recalculos, recalculosTotal, recalculosPagina, recalculosTamanhoPagina,
  onChangePaginaRecalculos, onChangeTamanhoPaginaRecalculos,
}) {
  const [expandidos, setExpandidos] = useState(new Set());

  useEffect(() => {
    setExpandidos(new Set());
  }, [recalculos]);

  const onToggleExpandir = (id) => {
    setExpandidos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) {
        proximo.delete(id);
      } else {
        proximo.add(id);
      }
      return proximo;
    });
  };

  const visiveis = recalculos || [];
  const inicio = recalculosPagina * recalculosTamanhoPagina;
  const ultimaPagina = recalculosTamanhoPagina > 0
    ? Math.ceil(recalculosTotal / recalculosTamanhoPagina) - 1
    : 0;

  return (
    <CardWrapper>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUNAS.map(coluna => (
                <TableCell key={coluna.campo} align={coluna.align}>{coluna.label}</TableCell>
              ))}
              <TableCell align="center" />
            </TableRow>
          </TableHead>
          <TableBody>
            {visiveis.map((recalculo, i) => {
              const expandido = expandidos.has(recalculo.id);
              return (
                <React.Fragment key={recalculo.id}>
                  <TableRow hover sx={{ backgroundColor: i % 2 === 1 ? C.paleGrey : undefined }}>
                    {COLUNAS.map(coluna => (
                      <TableCell key={coluna.campo} align={coluna.align}>
                        {coluna.campo === 'valorTotal' ? (
                          <Box component="span" sx={{ fontWeight: 700 }}>{recalculo[coluna.campo]}</Box>
                        ) : recalculo[coluna.campo]}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => onToggleExpandir(recalculo.id)}
                        aria-label={expandido ? 'Ocultar detalhes do recálculo' : 'Exibir detalhes do recálculo'}
                        sx={{ color: C.navy }}
                      >
                        {expandido ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={COLUNAS.length + 1} sx={{ py: 0, borderBottom: expandido ? undefined : 'none' }}>
                      <Collapse in={expandido} unmountOnExit>
                        <Grid container spacing={2} sx={{ py: 2, px: 1 }}>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2">
                              <b>Juros: </b>
                              {recalculo.porcentagemTaxaJuros}
                            </Typography>
                            <Typography variant="body2">
                              <b>Valor do Juros: </b>
                              {recalculo.valorJuros}
                            </Typography>
                            <Typography variant="body2">
                              <b>Tipo de Juros: </b>
                              {recalculo.utilizarJurosSimples}
                            </Typography>
                            <Typography variant="body2">
                              <b>Juros Único: </b>
                              {recalculo.calcularJurosUnico}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2">
                              <b>Honorários: </b>
                              {recalculo.porcentagemHonorarios}
                            </Typography>
                            <Typography variant="body2">
                              <b>Valor dos Honorários: </b>
                              {recalculo.valorHonorarios}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2">
                              <b>Descontos: </b>
                              {recalculo.porcentagemDesconto}
                            </Typography>
                            <Typography variant="body2">
                              <b>Valor do Desconto: </b>
                              {recalculo.valorDesconto}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2">
                              <b>Taxa do cartão: </b>
                              {recalculo.porcentagemTaxaCartao}
                            </Typography>
                            <Typography variant="body2">
                              <b>Valor da Taxa do cartão: </b>
                              {recalculo.valorTaxaCartao}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {visiveis.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUNAS.length + 1} align="center">
                  Não existem recálculos para o processo
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, mt: 2,
      }}
      >
        <Typography variant="body2">Linhas por página:</Typography>
        <Select
          size="small"
          value={recalculosTamanhoPagina}
          onChange={event => onChangeTamanhoPaginaRecalculos(event)}
          variant="standard"
          sx={{ minWidth: 56 }}
        >
          {[5, 10, 25].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
        <Typography variant="body2">
          {recalculosTotal > 0 ? inicio + 1 : 0}
          –
          {Math.min(inicio + recalculosTamanhoPagina, recalculosTotal)}
          {' '}
          de
          {recalculosTotal}
        </Typography>
        <IconButton
          size="small"
          disabled={recalculosPagina === 0}
          onClick={() => onChangePaginaRecalculos(recalculosPagina - 1)}
          sx={{ color: C.navy }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          size="small"
          disabled={recalculosPagina >= ultimaPagina}
          onClick={() => onChangePaginaRecalculos(recalculosPagina + 1)}
          sx={{ color: C.navy }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </CardWrapper>
  );
}

AbaRecalculos.propTypes = {
  recalculos: PropTypes.arrayOf(PropTypes.object),
  recalculosTotal: PropTypes.number,
  recalculosPagina: PropTypes.number,
  recalculosTamanhoPagina: PropTypes.number,
  onChangePaginaRecalculos: PropTypes.func,
  onChangeTamanhoPaginaRecalculos: PropTypes.func,
};

AbaRecalculos.defaultProps = {
  recalculos: [],
  recalculosTotal: 0,
  recalculosPagina: 0,
  recalculosTamanhoPagina: 5,
  onChangePaginaRecalculos: () => null,
  onChangeTamanhoPaginaRecalculos: () => null,
};
